<template>
  <modal ref="modal" :show-on-mount="true" :click-outside-to-close="false" headerless size="md"
         modal-id="missing-info-modal">
    <template #body>
      <form class="d-grid">
        <p v-if="showMissingFieldsIntro">
          {{ missingFieldsIntro }}
        </p>
        <p v-else-if="showDataProcessingConsent">
          {{ dataProcessingConsentIntro }}
        </p>

        <component :is="componentForField(field)" v-for="field of missingFields"
                   v-bind="propsForField(field)" :key="field" required entity-name="user"
                   :validation-errors="validationErrorsForField(field)"
                   @changed="(value) => fieldChanged(field, value)" />

        <eu-data-processing-consent v-if="showDataProcessingConsent" entity-name="user"
                                    :label="euDataProcessingConsentData.label"
                                    :show-label="showDataProcessingConsentLabelBeforeSubmit"
                                    input-name="user[eu_data_processing_consent]"
                                    :explicit-consent-required="euDataProcessingConsentData.explicitConsentRequired"
                                    :validation-errors="validationErrors.euDataProcessingConsent"
                                    @changed="(value) => fieldChanged('eu_data_processing_consent', value)" />

        <submit-button :label="i18n.t('administrative.continue')" :submit-in-progress="submitting" class="mt-3"
                       @click="submit" />

        <div v-if="showDataProcessingConsentLabelAfterSubmit" class="small-text mt-2"
             v-html="euDataProcessingConsentData.label" />
      </form>
    </template>
  </modal>
</template>

<script>
import AdditionalField from '../components/inputs/additional-field'
import ajax from '../helpers/ajax'
import camelCase from 'lodash/camelCase'
import EuDataProcessingConsent from '../components/inputs/eu-data-processing-consent.vue'
import FirstNameField from '../components/inputs/first-name-field'
import LastNameField from '../components/inputs/last-name-field'
import Modal from '../components/modal'
import PhoneNumberField from '../components/inputs/phone-number-field'
import PostcodeField from '../components/inputs/postcode-field'
import SubmitButton from '../components/buttons/submit-button'

const DATA_PROCESSING_FIELD_NAME = 'eu_data_processing_consent'

export default {
  components: { EuDataProcessingConsent, Modal, SubmitButton },

  props: {
    additionalFieldConfigs: {
      type: Array,
      required: true
    },

    euDataProcessingConsentData: {
      type: Object,
      required: false
    },

    requiredFields: {
      type: Array,
      required: true
    },

    phoneNumberLabel: {
      type: String,
      required: true
    },

    postcodeLabel: {
      type: String,
      required: false
    }
  },

  emits: ['completed'],

  data() {
    const userData = {}
    this.requiredFields.map((field) => {
      userData[field] = ''
    })

    return {
      submitting: false,
      userData: userData,
      validationErrors: {}
    }
  },

  computed: {
    dataProcessingConsentIntro() {
      return this.i18n.t('member.account.missing_info_modal.intro_data_processing_consent')
    },

    missingFields() {
      return this.requiredFields.filter(f => f !== DATA_PROCESSING_FIELD_NAME)
    },

    missingFieldsIntro() {
      return this.i18n.t('member.account.missing_info_modal.intro_missing_fields')
    },

    showDataProcessingConsent() {
      return this.requiredFields.includes(DATA_PROCESSING_FIELD_NAME)
    },

    showDataProcessingConsentLabelAfterSubmit() {
      if (!this.showDataProcessingConsent) {
        return false
      }

      if (this.euDataProcessingConsentData.explicitConsentRequired) {
        return false
      } else {
        return this.euDataProcessingConsentData.messagePosition === 'after_submit'
      }
    },

    showDataProcessingConsentLabelBeforeSubmit() {
      if (!this.showDataProcessingConsent) {
        return false
      }

      if (this.euDataProcessingConsentData.explicitConsentRequired) {
        return true
      } else {
        return this.euDataProcessingConsentData.messagePosition === 'before_submit'
      }
    },

    showMissingFieldsIntro() {
      return this.missingFields.length > 0
    }
  },

  methods: {
    componentForField(fieldName) {
      switch (fieldName) {
      case 'first_name':
        return FirstNameField
      case 'last_name':
        return LastNameField
      case 'phone_number':
        return PhoneNumberField
      case 'postcode':
        return PostcodeField
      default:
        return AdditionalField
      }
    },

    fieldChanged(fieldName, newValue) {
      this.userData[fieldName] = newValue
    },

    propsForField(fieldName) {
      switch (fieldName) {
      case 'first_name':
        return {}
      case 'last_name':
        return {}
      case 'phone_number':
        return {label: this.phoneNumberLabel}
      case 'postcode':
        return {label: this.postcodeLabel}
      default: {
        const fieldConfig = this.additionalFieldConfigs.find(config => config.name === fieldName)
        const { name, ...rest } = fieldConfig
        return {
          fieldName: name,
          ...rest
        }
      }}
    },

    submit() {
      this.submitting = true

      ajax.patch({
        url: '/account/missing_info',
        data: {
          user: this.userData
        },
        successHandler: ({ redirectUrl }) => {
          this.$emit('completed', { redirectUrl })
        },
        errorHandler: ({ info }) => {
          this.validationErrors = info.errors
        }
      }).then(() => {
        this.submitting = false
      })
    },

    validationErrorsForField(fieldName) {
      return this.validationErrors[camelCase(fieldName)]
    }
  }
}
</script>
